import axios from 'axios';
import qs from 'qs';
import {
    IMyCharListConfig,
    ICharacterList,
    ICharacterItem,
    ICharacterChangedParams,
} from '../interface/IMyCharList';
import { _getCharacterInfoByService } from './characterInfo';
import { getRegionTitle } from './regionL10n';

/**
 * @alias MyCharList
 * @category MyCharList
 * @param localeCountry {string} - optional
 * @param regionNameMap {object} - optional. 리전 코드→타이틀 맵. 있으면 리전별 아코디언 모드 (권장)
 * @param region {object} - optional. (레거시) { regionNameMap } 래퍼. 하위호환용
 * @example
 * if (window.ncui && window.ncui.myCharList) {
 *    const myCharList = new window.ncui.myCharList({ localeCountry: 'kr' });
 *    cnbConfig.charChangeFN = myCharList.setCharacterChangeFunction.bind(myCharList);
 * }
 * @example <caption>리전 모드 (권장: regionNameMap 직접)</caption>
 * const myCharList = new window.ncui.myCharList({
 *    localeCountry: 'ja-jp',
 *    // key = 노출 리전 코드 + 순서(첫 리전이 기본 펼침), value = 화면 타이틀
 *    regionNameMap: { as: 'アジア', nae: '北米-東部', naw: '北米-西部', eu: 'ヨーロッパ', la: '南米' },
 * });
 * @example <caption>리전 모드 (레거시: region 래퍼 — 여전히 동작)</caption>
 * const myCharList = new window.ncui.myCharList({
 *    localeCountry: 'ja-jp',
 *    region: { regionNameMap: { as: 'アジア', nae: '北米-東部' } },
 * });
 */

export default class Index {
    private ApiUrl: string;
    private onRequest: boolean = false;
    // 대표캐릭터 변경(SPA 콜백) 진행 중 표시하는 오버레이 스피너 엘리먼트
    private updatingOverlay: HTMLElement | null = null;
    private regionNameMap?: { [code: string]: string };
    // 대표캐릭터 변경 성공 후 실행할 콜백. 없으면 location.reload()
    // public: 생성자 옵션으로도, 인스턴스 생성 후 할당으로도 지정 가능
    public onCharacterChanged?: (
        params: ICharacterChangedParams
    ) => void | Promise<void>;
    // 리전별 캐릭터 리스트 캐시. 키 존재 = 호출 완료(빈 리스트여도 재호출 안 함)
    private regionCache = new Map<string, ICharacterList>();
    private purple = window.cnb?.getPurplePlatform
        ? window.cnb?.getPurplePlatform()
        : undefined;

    constructor({
        localeCountry,
        region,
        regionNameMap,
        onCharacterChanged,
    }: IMyCharListConfig) {
        // console.log('-------MyCharList START-------', _config);
        this.ApiUrl = `${location.origin}${
            localeCountry ? `/${localeCountry}` : ''
        }/my/profile`;
        // 신규(flat) regionNameMap 우선, 없으면 레거시 region.regionNameMap
        this.regionNameMap = regionNameMap ?? region?.regionNameMap;
        this.onCharacterChanged = onCharacterChanged;
    }

    // 캐릭터 리스트 불러오는 함수
    public async setCharacterChangeFunction(): Promise<void> {
        const characterWrapper = document.querySelector(
            '.ncc-character-panel .ncc-character-list'
        );
        if (!(characterWrapper instanceof HTMLUListElement)) return;

        // regionNameMap이 있으면 리전별 아코디언 모드
        if (this.regionNameMap) {
            this.renderRegionAccordion(characterWrapper);
            return;
        }

        // regionNameMap이 없으면 기존 동작 (전체 캐릭터를 한 번에 조회)
        try {
            const { data } = await axios.get(`${this.ApiUrl}/characters`);
            characterWrapper.innerHTML = this.markupCharacterList(data);
            this.setSelectCharacterEvent();
        } catch (error) {
            console.log(error);
        }
    }

    // 리전 아코디언 골격 렌더 + 기본 리전 펼침/로드
    private renderRegionAccordion(wrapper: HTMLUListElement): void {
        const codes = Object.keys(this.regionNameMap || {});
        if (codes.length === 0) return;

        // 맨 위에 펼칠 리전 우선순위:
        // 1) userData의 현재 리전 → 2) regionNameMap 첫 리전
        const currentRegion = this.getCurrentRegion();
        const openRegion = codes.includes(currentRegion)
            ? currentRegion
            : codes[0];

        const orderedCodes = [
            openRegion,
            ...codes.filter((code) => code !== openRegion),
        ];

        wrapper.innerHTML = orderedCodes
            .map((code, idx) => this.markupRegionItem(code, idx === 0))
            .join('');

        this.setRegionAccordionEvent(wrapper);
        // 맨 위 리전은 펼친 높이로 초기화 + 즉시 로드
        const openItem = wrapper.querySelector(
            `.ncc-character-region[data-region="${openRegion}"]`
        ) as HTMLElement | null;
        if (openItem) this.updateRegionHeight(openItem);
        this.loadRegion(openRegion);
    }

    // 현재 리전 추출: window.userData.gameAccount.subData 의 title==='region' 값
    // (subData 없음 / region 항목 없음 → 빈 문자열)
    private getCurrentRegion(): string {
        return (
            window.userData?.gameAccount?.subData?.find(
                (s) => s.title === 'region'
            )?.value ?? ''
        );
    }

    // 리전 아코디언 1개 (헤더 + 로딩 상태의 빈 바디)
    private markupRegionItem(code: string, isOpen: boolean): string {
        return `
            <li class="ncc-character-region${
                isOpen ? ' is-active' : ''
            }" data-region="${code}">
                <button type="button" class="ncc-character-region__head">
                    <span class="ncc-character-region__title">${getRegionTitle(
                        code,
                        this.regionNameMap
                    )}</span>
                    <span class="ncc-character-region__arrow"></span>
                </button>
                <ul class="ncc-character-region__body">
                    <li class="ncc-character-loading"><span class="ncc-character-loader-circle"></span></li>
                </ul>
            </li>`;
    }

    // 아코디언 헤더 클릭 (single-open: 하나 열면 나머지 닫힘 + 최초 펼침 시 로드)
    private setRegionAccordionEvent(wrapper: HTMLUListElement): void {
        wrapper
            .querySelectorAll('.ncc-character-region__head')
            .forEach((head: Element) => {
                head.addEventListener('click', () => {
                    const item = head.closest(
                        '.ncc-character-region'
                    ) as HTMLElement | null;
                    if (!item) return;

                    const willOpen = !item.classList.contains('is-active');

                    // single-open: 다른 열린 리전은 닫는다 (닫힘도 트랜지션)
                    wrapper
                        .querySelectorAll('.ncc-character-region.is-active')
                        .forEach((el: Element) => {
                            if (el !== item) {
                                el.classList.remove('is-active');
                                this.updateRegionHeight(el as HTMLElement);
                            }
                        });

                    item.classList.toggle('is-active', willOpen);
                    this.updateRegionHeight(item);

                    const code = item.getAttribute('data-region');
                    if (willOpen && code) this.loadRegion(code);
                });
            });
    }

    // 특정 리전 캐릭터 로드 (캐시 우선, 캐릭터 없으면 400 등으로 응답 → 빈 리스트)
    private async loadRegion(code: string): Promise<void> {
        const item = document.querySelector(
            `.ncc-character-region[data-region="${code}"]`
        );
        const body = item?.querySelector('.ncc-character-region__body');
        if (!(body instanceof HTMLElement)) return;

        // 한 번 호출한 리전은 재호출 없이 캐시로 렌더
        if (this.regionCache.has(code)) {
            this.renderRegionBody(body, this.regionCache.get(code)!, code);
            return;
        }

        let data: ICharacterList = {};
        try {
            const res = await axios.get(`${this.ApiUrl}/characters`, {
                params: { region: code },
            });
            data = res.data || {};
        } catch (error) {
            // 캐릭터 없는 리전은 400(security.NOT_EXIST_GAMEACCOUNT) 등으로 응답
            // → 아코디언은 열되 빈 리스트로 처리
            data = {};
        }
        this.regionCache.set(code, data);
        this.renderRegionBody(body, data, code);
    }

    // 리전 바디(서버 그룹 + 캐릭터) 렌더 + 선택 이벤트 바인딩
    private renderRegionBody(
        body: HTMLElement,
        data: ICharacterList,
        regionCode: string
    ): void {
        // 빈 리전이면 markup이 빈 문자열 → 로딩 스피너 제거되고 빈 바디 유지
        body.innerHTML = this.markupCharacterList(data, regionCode);
        this.setSelectCharacterEvent(body);

        // 콘텐츠 렌더 후 높이 재계산 → 이미 열린 영역보다 콘텐츠가 커지면 그 차이도 트랜지션
        const item = body.closest(
            '.ncc-character-region'
        ) as HTMLElement | null;
        if (item) this.updateRegionHeight(item);
    }

    // 리전 바디 높이를 상태에 맞춰 px로 갱신 (열림: 콘텐츠 높이, 닫힘: 0).
    // height 트랜지션이라 열고 닫힘 + 로드 후 콘텐츠 증가분까지 모두 부드럽게 처리됨
    private updateRegionHeight(item: HTMLElement): void {
        const body = item.querySelector(
            '.ncc-character-region__body'
        ) as HTMLElement | null;
        if (!body) return;

        if (item.classList.contains('is-active')) {
            // 빈 리전/로딩 시에도 최소 높이는 유지
            body.style.height = `${Math.max(body.scrollHeight, 64)}px`;
        } else {
            body.style.height = '0px';
        }
    }

    private markupCharacterList(
        charcterList: ICharacterList,
        regionCode?: string
    ): string {
        let characterListElement = '';
        Object.keys(charcterList)
            .map((key) => charcterList[key])
            .forEach((characters) => {
                let characterItem = '';
                characters.forEach((character: ICharacterItem, index) => {
                    if (index === 0)
                        characterItem += `<li class="ncc-character-item"><a href="#">${character.serverName}</a>`;
                    const characeterItem = `
                    <ul class="ncc-lnb-item__sub is-active">
                        <li class="cnb-character-list-items">
                            <div class="character-info">
                            <button type="button"
                                ${this.setGA360()}
                                data-setUserRepresentativeInfo-serverId="${
                                    character.serverId
                                }"
                                data-setUserRepresentativeInfo-characterId="${
                                    character.characterId
                                }"
                                ${
                                    regionCode
                                        ? `data-setUserRepresentativeInfo-region="${regionCode}"`
                                        : ''
                                }
                            >
                                <span class="character">
                                    ${_getCharacterInfoByService(character)}
                                </span>
                            </button>
                            </div>
                        </li>
                    </ul>`;
                    characterItem += characeterItem;
                });
                characterListElement += characterItem + '</li>';
            });
        return characterListElement;
    }

    private setGA360 = () => {
        if (!this.purple) return '';
        const { isPurple, type } = this.purple;
        let gaTag: string;
        gaTag = `data-area="퍼플 커뮤니티_${
            isPurple && type === 'purpleLauncher' ? 'PC 론처' : 'Mobile'
        }_CNB 클릭" data-contents="클릭 컨텐츠 : ${
            isPurple && type === 'purpleLauncher' ? 'PC 론처' : 'Mobile'
        }_CNB_우측 슬라이드 대표 캐릭터 선택"`;
        return gaTag;
    };
    // [data-setuserrepresentativeinfo-serverid]를 가진 태그에 클릭 이벤트
    // scope: 리전 모드에서는 새로 그린 리전 바디에만 바인딩(중복 등록 방지)
    private setSelectCharacterEvent(scope: ParentNode = document): void {
        scope
            .querySelectorAll('[data-setuserrepresentativeinfo-serverid]')
            .forEach((characterElement: Element) => {
                characterElement.addEventListener('click', () =>
                    this._selectCharacterEventHandler(characterElement)
                );
            });
    }

    // 캐릭터 클릭시 호출하는 API
    private async _selectCharacterEventHandler(
        characterElement: Element
    ): Promise<void> {
        if (this.onRequest) return;
        try {
            this.onRequest = true;
            // 클릭 직후(POST 시작 전)부터 오버레이 스피너를 켠다.
            // - 콜백 없음(reload 방식): POST 응답이 올 때까지 유지 → 아래 finally에서 끔.
            // - 콜백 있음(SPA 방식): POST + onCharacterChanged 완료까지 유지 → finally에서 끔.
            this.showUpdatingSpinner();
            const params: { [key: string]: string } = {
                serverId:
                    characterElement.getAttribute(
                        'data-setUserRepresentativeInfo-serverId'
                    ) || '',
                characterId:
                    characterElement.getAttribute(
                        'data-setUserRepresentativeInfo-characterid'
                    ) || '',
            };
            // 리전 모드면 대표캐릭터 변경 시 region 값도 함께 전달
            const region = characterElement.getAttribute(
                'data-setUserRepresentativeInfo-region'
            );
            if (region) params.region = region;

            await axios.post(
                `${this.ApiUrl}/userRepresentativeInfo`,
                qs.stringify(params),
                {
                    headers: {
                        Accept: 'application/json',
                        'Content-Type': 'application/x-www-form-urlencoded',
                    },
                }
            );
            // 콜백 있으면 위임(reload 안 함), 없으면 기존대로 전체 새로고침
            if (this.onCharacterChanged) {
                // 콜백 완료까지 await → 그동안 재선택 잠금 유지(중첩 실행/레이스 방지),
                // 콜백이 reject하면 아래 catch가 처리(floating promise 방지)
                await this.onCharacterChanged({
                    serverId: params.serverId,
                    characterId: params.characterId,
                    region: params.region,
                });
            } else {
                location.reload();
            }
        } catch (error) {
            console.error(error);
        } finally {
            // 콜백 방식은 페이지가 유지되므로 다음 변경을 위해 잠금 해제.
            // reload 방식은 어차피 페이지가 새로 로드되어 영향 없음.
            this.onRequest = false;
            // 콜백 방식: 콜백 완료 후 여기서 끔. reload 방식: location.reload() 직후
            // 여기서 끔(사실상 POST 응답 시점) — 곧 새 페이지가 로드되므로 영향 없음.
            this.hideUpdatingSpinner();
        }
    }

    // 대표캐릭터 변경 진행 중 풀스크린 오버레이 스피너 표시.
    // styleshop 다운로드 슬롯 RepresentativeCharacter의 __updating 디자인 참고:
    // 화면 전체 딤 + 중앙 흰색 로더 아이콘(8방향 스포크) 회전.
    // body 직속으로 붙여 대표 캐릭터 패널 위 전체를 덮는다.
    private showUpdatingSpinner(): void {
        if (this.updatingOverlay) return;
        const overlay = document.createElement('div');
        overlay.className = 'ncc-character-updating';
        overlay.innerHTML = `
            <svg class="ncc-character-updating__spinner" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-label="loading">
                <path d="M12 2v4"/>
                <path d="m16.2 7.8 2.9-2.9"/>
                <path d="M18 12h4"/>
                <path d="m16.2 16.2 2.9 2.9"/>
                <path d="M12 18v4"/>
                <path d="m4.9 19.1 2.9-2.9"/>
                <path d="M2 12h4"/>
                <path d="m4.9 4.9 2.9 2.9"/>
            </svg>`;
        document.body.appendChild(overlay);
        this.updatingOverlay = overlay;
    }

    private hideUpdatingSpinner(): void {
        this.updatingOverlay?.remove();
        this.updatingOverlay = null;
    }
} // end of class
